/**
 * MyMonthCard — iter258. Personal month-to-date strip for the Overview tab.
 * "You this month: X sessions · ₹Y my share · ₹Z fines · balance"
 */
import { useState, useEffect } from 'react';
import { CalendarCheck, ReceiptText, Flame, Scale } from 'lucide-react';
import { Card } from './ui/card';
import { formatNXC } from '../lib/formatNXC';
import api from '../lib/api';

export default function MyMonthCard({ groupId, currency = 'INR' }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    api.get(`/groups/${groupId}/my-month`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [groupId]);

  if (!data) return null;

  const monthName = new Date().toLocaleString('en-US', { month: 'long' });
  const bal = Math.round(data.my_balance || 0);
  const balLabel = bal < 0 ? `You owe ${formatNXC(Math.abs(bal), currency)}` : bal > 0 ? `Owed ${formatNXC(bal, currency)}` : 'Settled ✓';
  const balClass = bal < 0 ? 'text-rose-600' : bal > 0 ? 'text-emerald-600' : 'text-muted-foreground';

  return (
    <Card className="p-4 border-2 rounded-2xl" data-testid="my-month-card">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">
        You · {monthName}
      </p>
      <div className="grid grid-cols-4 gap-2">
        <MiniStat Icon={CalendarCheck} label="Sessions" value={String(data.sessions_attended)} testId="my-month-sessions" />
        <MiniStat Icon={ReceiptText} label="My share" value={formatNXC(Math.round(data.my_expense_share || 0), currency)} testId="my-month-share" />
        <MiniStat Icon={Flame} label="Fines" value={formatNXC(Math.round((data.my_fines || 0) + (data.my_session_fees || 0)), currency)} sub={data.my_session_fees > 0 ? 'incl. fees' : null} testId="my-month-fines" />
        <MiniStat Icon={Scale} label="Balance" value={balLabel} valueClass={balClass} testId="my-month-balance" />
      </div>
    </Card>
  );
}

function MiniStat({ Icon, label, value, sub, valueClass = '', testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground font-bold flex items-center gap-1">
        <Icon className="w-3 h-3 text-primary" />
        {label}
      </p>
      <p className={`text-sm font-bold tabular-nums mt-0.5 truncate ${valueClass}`} title={value}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
